import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Upload, Trash2, Edit, Eye, Share2, Package, ImagePlus, DollarSign, Loader2, Link, CreditCard } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { whatsappService } from "@/lib/whatsapp";
import { ProductShareCard } from "@/components/seller/ProductShareCard";

interface CatalogItem {
  id: string;
  title: string;
  description: string;
  price: number;
  images: string[];
  status: string;
  created_at: string;
}

export default function Catalog() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [catalogItems, setCatalogItems] = useState<CatalogItem[]>([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
  });
  const [generatingLink, setGeneratingLink] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) {
      fetchCatalogItems();
    }
  }, [user]);

  const fetchCatalogItems = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('seller_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching catalog:', error);
        throw error;
      }
      setCatalogItems(data || []);
    } catch (error: any) {
      console.error('Error fetching catalog:', error);
      toast.error(error.message || "Failed to load catalog items");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 5) {
      toast.error("Maximum 5 images allowed");
      return;
    }

    setSelectedImages(files);
    
    // Generate previews
    const previews = files.map(file => URL.createObjectURL(file));
    setImagePreviews(previews);
  };

  const removeImage = (index: number) => {
    const newImages = selectedImages.filter((_, i) => i !== index);
    const newPreviews = imagePreviews.filter((_, i) => i !== index);
    setSelectedImages(newImages);
    setImagePreviews(newPreviews);
  };

  const handleAddItem = async () => {
    if (!formData.title || !formData.price || selectedImages.length === 0) {
      toast.error("Please fill in all required fields and add at least one image");
      return;
    }

    setUploading(true);
    try {
      // Upload images
      const imageUrls: string[] = [];
      for (const image of selectedImages) {
        const fileExt = image.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const filePath = `${user?.id}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('products')
          .upload(filePath, image);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('products')
          .getPublicUrl(filePath);

        imageUrls.push(publicUrl);
      }

      // Create product
      const { error } = await supabase
        .from('products')
        .insert({
          seller_id: user?.id,
          title: formData.title,
          description: formData.description,
          price: parseFloat(formData.price),
          images: imageUrls,
          category: 'General',
          stock: 100,
          status: 'active'
        });

      if (error) throw error;

      toast.success("Item added to catalog successfully!");
      
      // Reset form
      setFormData({ title: "", description: "", price: "" });
      setSelectedImages([]);
      setImagePreviews([]);
      setShowAddDialog(false);
      
      // Refresh catalog
      fetchCatalogItems();
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error("Failed to add item to catalog");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteItem = async (id: string) => {
    if (!confirm("Are you sure you want to delete this item?")) return;

    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success("Item deleted successfully");
      fetchCatalogItems();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error("Failed to delete item");
    }
  };

  const generatePaymentLink = async (item: CatalogItem) => {
    setGeneratingLink(item.id);
    try {
      const { data, error } = await supabase.functions.invoke('generate-payment-link', {
        body: { productId: item.id, quantity: 1 }
      });

      if (error) throw error;

      const { paymentLink, trackingNumber } = data;
      
      // Create WhatsApp message with payment link
      const message = `🛍️ *${item.title}*\n\n` +
        `${item.description || 'Amazing product!'}\n\n` +
        `💰 *Price:* ₦${item.price.toLocaleString()}\n\n` +
        `📦 *Order Tracking:* ${trackingNumber || 'Available after payment'}\n\n` +
        `🔗 *Click to pay securely:*\n${paymentLink}\n\n` +
        `✅ Instant receipt via WhatsApp\n` +
        `📍 Real-time order tracking\n` +
        `🔒 Secure payment via Paystack`;
      
      const url = whatsappService.formatWhatsAppUrl('', message);
      window.open(url, '_blank');
      
      toast.success("Payment link generated! Share it on WhatsApp");
    } catch (error) {
      console.error('Error generating payment link:', error);
      toast.error("Failed to generate payment link");
    } finally {
      setGeneratingLink(null);
    }
  };

  const shareOnWhatsApp = (item: CatalogItem) => {
    const message = `Check out this product!\n\n*${item.title}*\n${item.description}\n\n💰 Price: ₦${item.price.toLocaleString()}\n\nOrder now: ${window.location.origin}/products/${item.id}`;
    const url = whatsappService.formatWhatsAppUrl(user?.whatsappNumber || '', message);
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Product Catalog</h1>
            <p className="text-muted-foreground mt-2">Manage your WhatsApp catalog items</p>
          </div>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button variant="gradient">
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add Catalog Item</DialogTitle>
                <DialogDescription>Add a new product to your WhatsApp catalog</DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {/* Image Upload */}
                <div>
                  <Label>Product Images (Max 5)</Label>
                  <div className="mt-2">
                    {imagePreviews.length > 0 ? (
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        {imagePreviews.map((preview, index) => (
                          <div key={index} className="relative group">
                            <img 
                              src={preview} 
                              alt={`Preview ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg"
                            />
                            <button
                              onClick={() => removeImage(index)}
                              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    
                    <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-muted-foreground/30 rounded-lg cursor-pointer hover:border-primary/50 transition-colors">
                      <div className="text-center">
                        <ImagePlus className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Click to upload images</span>
                      </div>
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                {/* Title */}
                <div>
                  <Label htmlFor="title">Product Title</Label>
                  <Input
                    id="title"
                    placeholder="e.g., Premium Leather Bag"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="mt-2"
                  />
                </div>

                {/* Description */}
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe your product..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-2 min-h-[100px]"
                  />
                </div>

                {/* Price */}
                <div>
                  <Label htmlFor="price">Price (₦)</Label>
                  <div className="relative mt-2">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="price"
                      type="number"
                      placeholder="0.00"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button 
                    onClick={handleAddItem} 
                    disabled={uploading}
                    className="flex-1"
                    variant="gradient"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Plus className="h-4 w-4 mr-2" />
                        Add to Catalog
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowAddDialog(false)}
                    disabled={uploading}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Catalog Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Total Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{catalogItems.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Active Items</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {catalogItems.filter(item => item.status === 'active').length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Total Value</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ₦{catalogItems.reduce((sum, item) => sum + item.price, 0).toLocaleString()}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Catalog Items */}
        {catalogItems.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <Package className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">No catalog items yet</h3>
                <p className="text-muted-foreground mb-4">Start adding products to your WhatsApp catalog</p>
                <Button variant="gradient" onClick={() => setShowAddDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Item
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {catalogItems.map((item) => (
              <Card key={item.id} className="overflow-hidden">
                {item.images && item.images[0] && (
                  <div className="h-48 bg-muted">
                    <img 
                      src={item.images[0]} 
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-lg">{item.title}</h3>
                    <Badge variant={item.status === 'active' ? 'default' : 'secondary'}>
                      {item.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {item.description || "No description"}
                  </p>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xl font-bold text-primary">
                      ₦{item.price.toLocaleString()}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(item.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="gradient" 
                      className="flex-1"
                      onClick={() => generatePaymentLink(item)}
                      disabled={generatingLink === item.id}
                    >
                      {generatingLink === item.id ? (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      ) : (
                        <CreditCard className="h-3 w-3 mr-1" />
                      )}
                      Payment Link
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => shareOnWhatsApp(item)}
                    >
                      <Share2 className="h-3 w-3" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={() => handleDeleteItem(item.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}